/**
 * RelatedInvoicesTable - Display related invoices for a customer
 */

import React, { useCallback } from "react";
import { Table, Tag, Button, Typography } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { getInvoiceDisplayStatus } from "../../invoices/utils/getInvoiceDisplayStatus.js";

const { Text, Link } = Typography;

const RelatedInvoicesTable = ({ invoices = [] }) => {
  const navigate = useNavigate();

  const viewInvoice = useCallback(
    (record) => {
      if (record.id) {
        navigate(`/invoices/${record.id}`);
      }
    },
    [navigate]
  );

  const formatCurrency = (amount) => {
    const value = Number(amount) || 0;
    return `R${value.toLocaleString("en-ZA", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const columns = [
    {
      title: "Invoice ID",
      dataIndex: "id",
      key: "id",
      render: (id, record) => (
        <Link
          onClick={(event) => {
            event.stopPropagation();
            viewInvoice(record);
          }}
        >
          <Text code>{id || "N/A"}</Text>
        </Link>
      ),
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      align: "right",
      render: (total) => total ? formatCurrency(total) : "N/A",
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => {
        const displayStatus = getInvoiceDisplayStatus(record);
        const statusMap = {
          draft: { color: "default", text: "Draft" },
          pending: { color: "gold", text: "Pending" },
          sent: { color: "blue", text: "Sent" },
          paid: { color: "green", text: "Paid" },
          partial: { color: "orange", text: "Partial" },
          overdue: { color: "red", text: "Overdue" },
          cancelled: { color: "default", text: "Cancelled" },
        };
        const cfg = statusMap[displayStatus] || { color: "default", text: displayStatus || "N/A" };
        return <Tag color={cfg.color}>{cfg.text}</Tag>;
      },
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => {
        if (!date) return "N/A";
        try {
          return format(new Date(date), "MMM dd, yyyy");
        } catch {
          return "Invalid date";
        }
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Button
          type="text"
          size="small"
          icon={<EyeOutlined />}
          aria-label="View invoice"
          onClick={(event) => {
            event.stopPropagation();
            viewInvoice(record);
          }}
          style={{ opacity: 0.6 }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.6")}
        />
      ),
    },
  ];

  if (invoices.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "20px" }}>
        <Text type="secondary">No invoices found for this customer</Text>
      </div>
    );
  }

  return (
    <Table
      columns={columns}
      dataSource={invoices}
      rowKey={(record) => record.id}
      pagination={{ pageSize: 10 }}
      size="small"
      scroll={{ x: "max-content" }}
      onRow={(record) => ({
        onClick: () => viewInvoice(record),
        style: { cursor: "pointer" },
      })}
    />
  );
};

export default RelatedInvoicesTable;

